import { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import { FileUploadDragAndDrop } from "@components/FormsV2/FileUploadDragAndDrop";
import fileToBase64 from "@utils/fileToBase64";
import {
  useCreateDataImportMutation,
  useV2GetAccountQuery,
  V1DataImportResource,
} from "@generated";

export interface ResourceImportModalProps {
  show: boolean;
  onClose: () => void;
  onError: (message: string) => void;
}

const appHost = process.env.REACT_APP_DOORKEEPER_APP_URL;

// Admin-context resource import, reusing the shared data-import pattern
// (useCreateDataImportMutation + FileUploadDragAndDrop + base64). Unlike the
// project-scoped DataImportModal, this targets the Account context and hands off
// to the admin column-mapping page once the data-import is created.
export const ResourceImportModal = ({
  show,
  onClose,
  onError,
}: ResourceImportModalProps): JSX.Element => {
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const { data: account } = useV2GetAccountQuery({}, {
    skip: !accountSlug,
  });
  const [createDataImport] = useCreateDataImportMutation();
  const [uploading, setUploading] = useState(false);

  const accountId = account?.data?.attributes?.["account-id"];

  const handleFileSelect = async (file: File) => {
    if (!file || !accountId) return;
    const encodedFile = await fileToBase64(file);
    if (!encodedFile) return;

    const importData: V1DataImportResource = {
      type: "data-imports",
      attributes: {
        "import-type": "resources",
        "context-type": "Account",
        "context-id": accountId.toString(),
        "data-file": encodedFile,
      },
      relationships: {
        account: { data: { type: "accounts", id: accountId } },
      },
    };

    setUploading(true);
    try {
      const res = await createDataImport({
        slug: accountSlug,
        body: { data: importData },
      }).unwrap();
      const importId = res.data?.id;
      onClose();
      // Hand off to the admin import column-mapping screen (Rails app host).
      window.location.assign(`${appHost}/admin/imports/${importId}/edit`);
    } catch {
      setUploading(false);
      onError(
        "Could not start the import. Please check the file and try again."
      );
    }
  };

  return (
    <ScopeStackModal
      modalTitle="Import Resources"
      modalBody={
        <div>
          <div className="instructionalText seafoamText bold">
            Upload a CSV or Excel file of resources. You'll map the columns on
            the next screen.
          </div>
          <FileUploadDragAndDrop handleFileSelect={handleFileSelect} />
          {uploading && <div className="mt-2">Uploading…</div>}
        </div>
      }
      button1Text="Cancel"
      handleButton1Click={onClose}
      button2Text={null}
      handleButton2Click={() => null}
      show={show}
      handleClose={onClose}
    />
  );
};

export default ResourceImportModal;
